// frontend-react/src/components/PDFViewer.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Space, message, InputNumber } from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import * as pdfjsLib from 'pdfjs-dist';
import { TextItem } from 'pdfjs-dist/types/src/display/api';
import { TextMatcher } from '../utils/textMatcher';

// Configure PDF.js worker - compatible with both development and production
pdfjsLib.GlobalWorkerOptions.workerSrc =
  process.env.NODE_ENV === 'production'
    ? `${window.location.origin}/pdf.worker.min.js` // Use local worker file in production
    : new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

interface ReviewItem {
  item_id: string;
  page: string;
  line?: string;
  rule: string;
  origin: string;
  suggestion: string;
}

interface PDFViewerProps {
  pdfData: ArrayBuffer;
  reviewResults: ReviewItem[];
  onScaleChange?: (scale: number) => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfData, onScaleChange }) => {
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pagesTextContent = useRef<Map<number, string>>(new Map());
  const pagesData = useRef<Map<number, { textContent: any; viewport: any }>>(new Map());
  const renderIdRef = useRef(0);

  // 縮放範圍
  const MIN_SCALE = 0.5;
  const MAX_SCALE = 1.5;
  const SCALE_STEP = 0.25;

  // 通知父元件縮放變化
  useEffect(() => {
    onScaleChange?.(scale);
  }, [scale, onScaleChange]);

  // 輔助函數：為一組 span 創建高亮
  const createHighlightForSpans = useCallback(
    (spans: HTMLElement[], container: HTMLElement, riskLevel: string) => {
      const containerRect = container.getBoundingClientRect();

      // 計算所有 span 的邊界框
      let minLeft = Infinity,
        minTop = Infinity;
      let maxRight = -Infinity,
        maxBottom = -Infinity;

      spans.forEach(span => {
        const rect = span.getBoundingClientRect();
        minLeft = Math.min(minLeft, rect.left);
        minTop = Math.min(minTop, rect.top);
        maxRight = Math.max(maxRight, rect.right);
        maxBottom = Math.max(maxBottom, rect.bottom);
      });

      // 創建單一高亮框覆蓋所有 span
      const highlight = document.createElement('div');
      highlight.className = `pdf-highlight-overlay risk-${riskLevel}`;

      const yOffset = 5;
      const left = minLeft - containerRect.left;
      const top = minTop - containerRect.top + yOffset;
      const width = maxRight - minLeft;
      const height = maxBottom - minTop;

      highlight.style.cssText = `
      position: absolute;
      left: ${left}px;
      top: ${top}px;
      width: ${width}px;
      height: ${height}px;
      background-color: ${
        riskLevel === 'high'
          ? 'rgba(220, 38, 38, 0.3)'
          : riskLevel === 'medium'
            ? 'rgba(217, 119, 6, 0.3)'
            : 'rgba(22, 163, 74, 0.3)'
      };
      border: 2px solid ${
        riskLevel === 'high'
          ? 'var(--color-error)'
          : riskLevel === 'medium'
            ? 'var(--color-warning)'
            : 'var(--color-success)'
      };
      border-radius: 3px;
      pointer-events: none;
      z-index: 10;
    `;

      container.appendChild(highlight);
    },
    []
  );

  const loadPDF = useCallback(async () => {
    if (!pdfData) return;

    setIsLoading(true);
    try {
      // Create a copy of the ArrayBuffer to avoid detached buffer issues
      const pdfDataCopy = pdfData.slice(0);
      const doc = await pdfjsLib.getDocument({
        data: pdfDataCopy,
        // Disable worker to avoid ArrayBuffer transfer issues
        useWorkerFetch: false,
        isEvalSupported: false,
        useSystemFonts: true,
        // CMap configuration for Chinese font support
        cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
        cMapPacked: true,
      }).promise;
      setPdfDoc(doc);
      setTotalPages(doc.numPages);

      // Clear previous text content
      pagesTextContent.current.clear();
    } catch (error) {
      console.error('Failed to load PDF:', error);
      message.error('PDF 載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [pdfData]);

  const renderPage = useCallback(
    async (pageNum: number, container: HTMLElement, customScale?: number) => {
      if (!pdfDoc) return;
      const renderIdAtStart = renderIdRef.current;

      try {
        const page = await pdfDoc.getPage(pageNum);
        if (renderIdRef.current !== renderIdAtStart) return;
        const useScale = customScale ?? scale;
        const viewport = page.getViewport({ scale: useScale });

        // Create page container — 用 aspect-ratio 自適應容器寬度
        const pageContainer = document.createElement('div');
        pageContainer.className = 'pdf-page-wrapper';
        pageContainer.dataset.pageNumber = pageNum.toString();
        pageContainer.style.position = 'relative';
        pageContainer.style.width = '100%';
        pageContainer.style.aspectRatio = `${viewport.width} / ${viewport.height}`;

        // Create canvas — 渲染用原始解析度，CSS 縮放填滿
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = '100%';
        canvas.style.height = '100%';

        // Create text layer — 同樣用 CSS 縮放
        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'textLayer';
        textLayerDiv.style.width = `${viewport.width}px`;
        textLayerDiv.style.height = `${viewport.height}px`;
        textLayerDiv.style.transformOrigin = 'top left';
        textLayerDiv.style.transform = `scale(var(--pdf-css-scale, 1))`;
        textLayerDiv.style.setProperty('--scale-factor', useScale.toString());

        // Create highlight layer
        const highlightLayerDiv = document.createElement('div');
        highlightLayerDiv.className = 'highlightLayer';
        highlightLayerDiv.dataset.pageNumber = pageNum.toString();

        pageContainer.appendChild(canvas);
        pageContainer.appendChild(textLayerDiv);
        pageContainer.appendChild(highlightLayerDiv);
        container.appendChild(pageContainer);

        // 計算 CSS 縮放比（容器實際寬度 vs 原始 viewport 寬度）
        const cssScale = pageContainer.clientWidth / viewport.width;
        textLayerDiv.style.setProperty('--pdf-css-scale', cssScale.toString());
        highlightLayerDiv.style.transformOrigin = 'top left';
        highlightLayerDiv.style.transform = `scale(${cssScale})`;
        highlightLayerDiv.style.width = `${viewport.width}px`;
        highlightLayerDiv.style.height = `${viewport.height}px`;

        // Render canvas
        const renderContext = {
          canvasContext: canvas.getContext('2d')!,
          viewport: viewport,
        };
        await page.render(renderContext).promise;

        // Render text layer
        const textContent = await page.getTextContent();
        const textLayerRender = pdfjsLib.renderTextLayer({
          textContentSource: textContent,
          container: textLayerDiv,
          viewport: viewport,
          textDivs: [],
        });
        await textLayerRender.promise;

        // Store text content for searching
        const pageText = textContent.items
          .filter((item): item is TextItem => 'str' in item)
          .map(item => item.str)
          .join(' ');
        pagesTextContent.current.set(pageNum, pageText);

        // Store page data for precise highlighting
        pagesData.current.set(pageNum, {
          textContent,
          viewport,
        });
      } catch (error) {
        console.error(`Failed to render page ${pageNum}:`, error);
      }
    },
    [pdfDoc, scale]
  );

  const renderAllPages = useCallback(async () => {
    if (!pdfDoc || !containerRef.current) return;

    const renderId = ++renderIdRef.current;
    const container = containerRef.current;
    container.innerHTML = '';

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      if (renderIdRef.current !== renderId) return;
      await renderPage(pageNum, container);
    }
  }, [pdfDoc, totalPages, renderPage]);

  const scrollToPage = (pageNum: number) => {
    const pageElement = containerRef.current?.querySelector(
      `[data-page-number="${pageNum}"]`
    ) as HTMLElement;
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return pageElement;
    }
    return null;
  };

  // 改進的高亮算法 - 解決整頁高亮問題
  const highlightText = useCallback(
    (pageNum: number, targetText: string, riskLevel: string) => {
      console.log(`🎯 智能高亮文字: 第${pageNum}頁 - "${targetText}"`);

      // 1. 清除所有現有高亮
      document.querySelectorAll('.pdf-highlight-overlay').forEach(el => el.remove());

      // 2. 獲取頁面容器和文字層
      const pageContainer = containerRef.current?.querySelector(
        `[data-page-number="${pageNum}"]`
      ) as HTMLElement;

      if (!pageContainer) {
        console.error('找不到頁面容器');
        return;
      }

      const textLayer = pageContainer.querySelector('.textLayer');
      if (!textLayer) {
        console.error('找不到文字層');
        return;
      }

      // 3. 優先使用智能匹配系統
      const match = TextMatcher.smartMatch(textLayer, targetText);

      // 只使用高品質的智能匹配結果
      if (
        match &&
        ((match.matchType === 'exact' && match.confidence > 0.9) ||
          (match.matchType === 'partial' && match.confidence > 0.8 && match.spans.length <= 3))
      ) {
        console.log(`✅ 高品質智能匹配:`, {
          type: match.matchType,
          confidence: `${(match.confidence * 100).toFixed(1)}%`,
          spans: match.spans.length,
          text: match.matchedText.substring(0, 50) + (match.matchedText.length > 50 ? '...' : ''),
        });

        createHighlightForSpans(match.spans, pageContainer, riskLevel);
        return; // 成功匹配，直接返回
      } else if (match) {
        console.log(
          `⚠️ 智能匹配品質不佳，忽略: ${match.matchType}, 信心度: ${(match.confidence * 100).toFixed(1)}%`
        );
      }

      // 4. 如果智能匹配失敗，回退到原有邏輯
      console.log(`⚠️ 智能匹配失敗，回退到傳統匹配...`);

      // 4. 文字清理和分類
      const cleanTargetText = targetText.replace(/[\s\u200B-\u200D\uFEFF]/g, '').trim();
      const isShortText = cleanTargetText.length <= 3 || /^註\d+$/.test(cleanTargetText);
      const isVeryLongText = cleanTargetText.length > 50; // 新增：超長文字判斷

      // 5. 短文字特殊處理
      if (isShortText) {
        console.log(`⚠️ 短文字精確匹配模式 - 目標: "${targetText}"`);

        const textSpans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];

        // 只匹配完全相同的單個 span
        for (const span of textSpans) {
          const spanText = (span.textContent || '').trim();
          const cleanSpanText = spanText.replace(/[\s\u200B-\u200D\uFEFF]/g, '').trim();

          if (
            spanText === targetText.trim() ||
            cleanSpanText === cleanTargetText ||
            spanText === cleanTargetText ||
            cleanSpanText === targetText.trim()
          ) {
            console.log(`✅ 短文字精確匹配成功: "${spanText}"`);
            createHighlightForSpans([span], pageContainer, riskLevel);
            return;
          }
        }

        // 嘗試兩個連續span的組合
        for (let i = 0; i < textSpans.length - 1; i++) {
          const span1 = textSpans[i];
          const span2 = textSpans[i + 1];
          if (!span1 || !span2) continue;

          const combinedText = ((span1.textContent || '') + (span2.textContent || '')).trim();
          const cleanCombinedText = combinedText.replace(/[\s\u200B-\u200D\uFEFF]/g, '').trim();

          if (combinedText === targetText.trim() || cleanCombinedText === cleanTargetText) {
            console.log(`✅ 短文字雙span匹配成功: "${combinedText}"`);
            createHighlightForSpans([span1, span2], pageContainer, riskLevel);
            return;
          }
        }

        console.warn(`❌ 無法精確匹配短文字: "${targetText}"`);
        return;
      }

      // 6. 標準化目標文字 (更保守的清理)
      const normalizeText = (text: string) => {
        return text
          .replace(/\s+/g, '') // 移除空白
          .replace(/[\u200B-\u200D\uFEFF]/g, '') // 移除不可見字符
          .replace(/[（）()]/g, ''); // 只移除括號，保留其他標點符號
      };

      const normalizedTarget = normalizeText(targetText);
      console.log(`🔍 正規化目標: "${normalizedTarget}" (長度: ${normalizedTarget.length})`);

      const textSpans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];
      let foundMatch = false;

      // 7. 策略 A：平衡的連續 span 匹配
      const maxSpansToCheck = isVeryLongText ? 8 : 6; // 適中的span數量

      for (let i = 0; i < textSpans.length; i++) {
        for (let length = 1; length <= Math.min(maxSpansToCheck, textSpans.length - i); length++) {
          const spanGroup = textSpans.slice(i, i + length);
          const combinedText = spanGroup.map(s => s.textContent || '').join('');
          const normalizedCombined = normalizeText(combinedText);

          // 更精確的匹配條件
          if (
            normalizedCombined === normalizedTarget ||
            (normalizedCombined.includes(normalizedTarget) &&
              normalizedTarget.length > 5 &&
              normalizedCombined.length / normalizedTarget.length <= 1.8)
          ) {
            console.log(`✅ 精確匹配成功 (${length}個span): "${combinedText.substring(0, 50)}..."`);
            createHighlightForSpans(spanGroup, pageContainer, riskLevel);
            foundMatch = true;
            return;
          }
        }
      }

      // 8. 策略 B：謹慎的部分匹配
      if (!foundMatch && normalizedTarget.length >= 10 && normalizedTarget.length <= 40) {
        const partialTarget = normalizedTarget.substring(
          0,
          Math.floor(normalizedTarget.length * 0.75)
        ); // 75%的內容必須匹配
        console.log(`🔄 部分匹配嘗試: "${partialTarget}"`);

        for (let i = 0; i < textSpans.length; i++) {
          for (let length = 1; length <= Math.min(5, textSpans.length - i); length++) {
            const spanGroup = textSpans.slice(i, i + length);
            const combinedText = spanGroup.map(s => s.textContent || '').join('');
            const normalizedCombined = normalizeText(combinedText);

            if (
              normalizedCombined.includes(partialTarget) &&
              normalizedCombined.length / partialTarget.length <= 2.0 // 長度不能差太多
            ) {
              console.log(`⚠️ 部分匹配成功: "${combinedText.substring(0, 50)}..."`);
              createHighlightForSpans(spanGroup, pageContainer, riskLevel);
              foundMatch = true;
              return;
            }
          }
        }
      }

      // 9. 策略 C：精準關鍵詞匹配
      if (!foundMatch && normalizedTarget.length >= 4) {
        // 提取較長的關鍵詞
        const chineseKeywords = normalizedTarget.match(/[\u4e00-\u9fff]{4,}/g);
        const englishKeywords = normalizedTarget.match(/[a-zA-Z]{5,}/g);
        const allKeywords = [...(chineseKeywords || []), ...(englishKeywords || [])];

        if (allKeywords && allKeywords.length > 0) {
          console.log(`🔍 嘗試關鍵詞匹配:`, allKeywords);

          // SonarQube Fix: Provide an initial value for reduce()
          const longestKeyword = allKeywords.reduce((a, b) => (a.length > b.length ? a : b), '');

          for (let i = 0; i < textSpans.length; i++) {
            for (let length = 1; length <= Math.min(4, textSpans.length - i); length++) {
              const spanGroup = textSpans.slice(i, i + length);
              const combinedText = spanGroup.map(s => s.textContent || '').join('');
              const normalizedCombined = normalizeText(combinedText);

              if (
                normalizedCombined.includes(longestKeyword) &&
                normalizedCombined.length <= longestKeyword.length * 2.5 // 嚴格控制長度
              ) {
                console.log(
                  `✅ 關鍵詞匹配成功: "${longestKeyword}" -> "${combinedText.substring(0, 50)}..."`
                );
                createHighlightForSpans(spanGroup, pageContainer, riskLevel);
                foundMatch = true;
                return;
              }
            }
          }
        }
      }

      // 10. 超長文字的特殊處理：只匹配開頭部分
      if (!foundMatch && isVeryLongText) {
        const startPortion = normalizedTarget.substring(0, 12); // 只取開頭12個字符
        console.log(`🔄 超長文字開頭匹配: "${startPortion}"`);

        for (let i = 0; i < textSpans.length; i++) {
          for (let length = 1; length <= 3; length++) {
            // 限制為3個span
            const spanGroup = textSpans.slice(i, i + length);
            const combinedText = spanGroup.map(s => s.textContent || '').join('');
            const normalizedCombined = normalizeText(combinedText);

            if (
              normalizedCombined.startsWith(startPortion) ||
              (normalizedCombined.includes(startPortion) &&
                normalizedCombined.length <= startPortion.length * 2)
            ) {
              console.log(`✅ 超長文字開頭匹配成功: "${combinedText.substring(0, 50)}..."`);
              createHighlightForSpans(spanGroup, pageContainer, riskLevel);
              foundMatch = true;
              return;
            }
          }
        }
      }

      if (!foundMatch) {
        console.warn(`❌ 無法找到匹配文字: "${targetText.substring(0, 100)}..."`);
        console.warn(`🔍 建議：檢查PDF內容是否與審查結果對應，或文字可能跨越多行`);
        message.warning('無法精確定位文字，建議手動查找對應內容');
      }
    },
    [createHighlightForSpans]
  );

  const jumpToText = useCallback(
    (pageNum: number, text: string, riskLevel: string) => {
      // SonarQube Fix: Use Number.parseInt with radix
      const pageNumInt = Number.parseInt(pageNum.toString(), 10);

      // SonarQube Fix: Use Number.isNaN
      if (Number.isNaN(pageNumInt) || pageNumInt < 1 || pageNumInt > totalPages) {
        message.error('無效的頁碼');
        return;
      }

      console.log(`🎯 跳轉到第 ${pageNumInt} 頁，高亮文字: "${text}"`);

      const pageElement = scrollToPage(pageNumInt);
      if (pageElement) {
        // Immediately highlight, then wait for scroll to complete for better effect
        highlightText(pageNumInt, text, riskLevel);

        // Add a subtle page flash effect
        pageElement.style.transition = 'background-color 0.5s ease';
        pageElement.style.backgroundColor = 'rgba(217, 119, 6, 0.1)';
        setTimeout(() => {
          pageElement.style.backgroundColor = '';
        }, 1500);
      }
    },
    [totalPages, highlightText]
  );

  // Expose jumpToText function to parent
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).jumpToText = jumpToText;
    }
  }, [jumpToText]);

  // 添加調試工具函數
  useEffect(() => {
    // 暴露調試函數到window
    (window as any).pdfDebug = {
      // 顯示所有文字項目的邊界框
      showTextBounds: (pageNum: number) => {
        console.log(`🔧 顯示第 ${pageNum} 頁的文字邊界`);

        const pageContainer = containerRef.current?.querySelector(
          `[data-page-number="${pageNum}"]`
        );
        if (!pageContainer) {
          console.error('找不到頁面容器');
          return;
        }

        // 清除之前的調試框
        document.querySelectorAll('.debug-text-bound').forEach(el => el.remove());

        const textSpans = pageContainer.querySelectorAll('.textLayer span');
        console.log(`找到 ${textSpans.length} 個文字span`);

        textSpans.forEach((span, index) => {
          const rect = span.getBoundingClientRect();
          const containerRect = pageContainer.getBoundingClientRect();

          const debugBox = document.createElement('div');
          debugBox.className = 'debug-text-bound';
          debugBox.style.cssText = `
            position: absolute;
            left: ${rect.left - containerRect.left}px;
            top: ${rect.top - containerRect.top}px;
            width: ${rect.width}px;
            height: ${rect.height}px;
            border: 1px solid red;
            pointer-events: none;
            z-index: 1000;
            background: rgba(255, 0, 0, 0.1);
          `;

          const label = document.createElement('div');
          label.textContent = `${index}: ${span.textContent}`;
          label.style.cssText = `
            position: absolute;
            top: -20px;
            left: 0;
            font-size: 10px;
            background: yellow;
            padding: 2px;
            white-space: nowrap;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
          `;

          debugBox.appendChild(label);
          pageContainer.appendChild(debugBox);
        });

        console.log('✅ 調試邊界框已顯示');

        // 10秒後自動清除
        setTimeout(() => {
          document.querySelectorAll('.debug-text-bound').forEach(el => el.remove());
        }, 10000);
      },

      // 列出頁面所有文字內容
      listPageText: (pageNum: number) => {
        console.log(`📝 列出第 ${pageNum} 頁的所有文字`);

        const pageData = pagesData.current.get(pageNum);
        if (!pageData) {
          console.error('找不到頁面數據');
          return;
        }

        console.table(
          pageData.textContent.items.map((item: any, index: number) => ({
            index,
            text: item.str,
            x: item.transform[4],
            y: item.transform[5],
            width: item.width,
            height: item.height,
            fontSize: item.transform[0],
          }))
        );

        // 也列出textLayer中的span
        const pageContainer = containerRef.current?.querySelector(
          `[data-page-number="${pageNum}"]`
        );
        const textSpans = pageContainer?.querySelectorAll('.textLayer span');

        if (textSpans) {
          console.log(`🔤 TextLayer spans (${textSpans.length}個):`);
          console.table(
            Array.from(textSpans).map((span, index) => ({
              index,
              text: span.textContent,
              left: (span as HTMLElement).style.left,
              top: (span as HTMLElement).style.top,
              fontSize: (span as HTMLElement).style.fontSize,
            }))
          );
        }
      },

      // 測試高亮特定索引的文字
      highlightByIndex: (pageNum: number, index: number) => {
        console.log(`🎯 高亮第 ${pageNum} 頁的第 ${index} 個文字項目`);

        const pageData = pagesData.current.get(pageNum);
        if (!pageData) {
          console.error('找不到頁面數據');
          return;
        }

        const item = pageData.textContent.items[index];
        if (!item) {
          console.error(`找不到索引 ${index} 的文字項目`);
          return;
        }

        console.log(`將高亮文字: "${item.str}"`);
        highlightText(pageNum, item.str, 'medium');
      },

      // 清除所有調試元素
      clearDebug: () => {
        document
          .querySelectorAll('.debug-text-bound, .pdf-highlight-overlay')
          .forEach(el => el.remove());
        console.log('🧹 調試元素已清除');
      },

      // 測試特定文字高亮
      testHighlight: (
        pageNum: number,
        text: string,
        riskLevel: 'high' | 'medium' | 'low' = 'medium'
      ) => {
        console.log(`🧪 測試高亮: 第${pageNum}頁 - "${text}" (${riskLevel})`);
        highlightText(pageNum, text, riskLevel);
      },

      // 測試問題案例
      testProblematicCases: (pageNum: number) => {
        console.log(`🔧 測試問題案例 - 第${pageNum}頁`);

        const testCases = [
          '圖片(TOYOTA TOWN ACE 發財雙王牌影音安全雙進化!註2限時免費大升級,再享最高 50 萬 50期0利率)',
          '「音場頂級節奏隨行」註1、「TOWN ACE 發財雙王牌影音安全雙進化」註2優惠活動自即日起至3月底止,贈品數量有限,送完為止。',
          '※註2:「TOYOTA TOWN ACE 發財雙王牌影音安全雙進化」:TOWN ACE於113年1月1日~114年1月 31 日為輕型商用車市場銷售冠軍',
        ];

        testCases.forEach((testCase, index) => {
          setTimeout(() => {
            console.log(`\n--- 測試案例 ${index + 1} ---`);
            console.log(`文字: "${testCase.substring(0, 50)}..."`);
            console.log(`長度: ${testCase.length}`);
            highlightText(pageNum, testCase, 'medium');
          }, index * 3000); // 每3秒測試一個案例
        });
      },
    };

    console.log('🛠️ PDF調試工具已載入，使用方法:');
    console.log('window.pdfDebug.showTextBounds(頁碼) - 顯示文字邊界');
    console.log('window.pdfDebug.listPageText(頁碼) - 列出頁面文字');
    console.log('window.pdfDebug.highlightByIndex(頁碼, 索引) - 高亮指定文字');
    console.log('window.pdfDebug.testHighlight(頁碼, 文字, 風險等級) - 測試高亮');
    console.log('window.pdfDebug.testProblematicCases(頁碼) - 測試問題案例');
    console.log('window.pdfDebug.clearDebug() - 清除調試元素');
  }, [pdfDoc, highlightText]);

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  // 縮放控制
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + SCALE_STEP, MAX_SCALE));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - SCALE_STEP, MIN_SCALE));
  };

  // 頁碼控制
  const goToPage = (pageNum: number) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      scrollToPage(pageNum);
    }
  };

  // SonarQube Fix: Remove unused argument 'e'
  const handleGoToPrevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  // SonarQube Fix: Remove unused argument 'e'
  const handleGoToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  // 監聽滾動更新當前頁碼
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const pages = container.querySelectorAll('.pdf-page-wrapper');

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement;
      const pageTop = page.offsetTop;
      const pageHeight = page.offsetHeight;

      if (scrollTop >= pageTop - 50 && scrollTop < pageTop + pageHeight - 50) {
        // SonarQube Fix: Use Number.parseInt
        const pageNum = Number.parseInt(page.dataset.pageNumber || '1', 10);
        if (pageNum !== currentPage) {
          setCurrentPage(pageNum);
        }
        break;
      }
    }
  }, [currentPage]);

  // 當縮放改變時重新渲染所有頁面
  useEffect(() => {
    if (pdfDoc && containerRef.current) {
      renderAllPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      const ref = renderIdRef;
      ref.current++;
    };
  }, [scale, pdfDoc, renderAllPages]);

  useEffect(() => {
    loadPDF();
  }, [loadPDF]);

  if (isLoading) {
    return (
      <div
        className='loading-state'
        style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className='loading-spinner'></div>
        正在載入 PDF...
      </div>
    );
  }

  if (!pdfDoc) {
    return (
      <div
        className='empty-state'
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className='empty-state-icon'>📄</div>
        <div>請上傳文件</div>
        <div style={{ fontSize: '12px', marginTop: '8px' }}>文件內容將顯示在此</div>
      </div>
    );
  }

  return (
    // ⭐ 關鍵修改：外層容器設定 height: 100% 來填滿 Card body
    <div
      style={{
        height: '100%', // ⭐ 填滿父容器高度
        width: '100%', // ⭐ 新增
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // ⭐ 新增
      }}
    >
      {/* 控制列 */}
      <div
        className='pdf-controls'
        style={{
          padding: '8px 12px',
          background: '#fafafa',
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          flexShrink: 0, // ⭐ 控制列不縮小
        }}
      >
        {/* 頁碼 + 縮放控制 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            size='small'
            icon={<LeftOutlined />}
            onClick={handleGoToPrevPage}
            disabled={currentPage <= 1}
          />
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <InputNumber
              size='small'
              min={1}
              max={totalPages}
              value={currentPage}
              onChange={value => value && goToPage(value)}
              style={{ width: '60px' }}
            />
            <span style={{ color: '#666' }}>/ {totalPages} 頁</span>
          </span>
          <Button
            size='small'
            icon={<RightOutlined />}
            onClick={handleGoToNextPage}
            disabled={currentPage >= totalPages}
          />

          <span style={{ color: '#d9d9d9', margin: '0 4px' }}>|</span>

          <Button
            size='small'
            icon={<ZoomOutOutlined />}
            onClick={handleZoomOut}
            disabled={scale <= MIN_SCALE}
          />
          <span
            style={{
              color: '#666',
              width: '48px',
              textAlign: 'center',
              fontSize: '13px',
              flexShrink: 0,
            }}
          >
            {Math.round(scale * 100)}%
          </span>
          <Button
            size='small'
            icon={<ZoomInOutlined />}
            onClick={handleZoomIn}
            disabled={scale >= MAX_SCALE}
          />
        </div>

        {/* 快速導航 */}
        <div>
          <Space size='small'>
            <Button size='small' icon={<VerticalAlignTopOutlined />} onClick={scrollToTop} />
            <Button size='small' icon={<VerticalAlignBottomOutlined />} onClick={scrollToBottom} />
          </Space>
        </div>
      </div>

      {/* ⭐ PDF 內容區域 - 關鍵修改 */}
      <div ref={containerRef} className='pdf-container' onScroll={handleScroll}>
        {/* PDF pages will be rendered here */}
      </div>
    </div>
  );
};

export default PDFViewer;
